import React, { useState, useEffect } from 'react';
import { getDiscardedForms, getDiscardedFormsStats, deleteDiscardedFormById } from '../utils/discardedForms';
import AdminLayout from '../layouts/AdminLayout';
import { FiFileText, FiX, FiAlertCircle, FiTrendingDown, FiPieChart, FiRefreshCw, FiTrash2, FiDownload } from 'react-icons/fi';

const DiscardedForms = () => {
  const [discardedForms, setDiscardedForms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    // Fetch discarded forms
    const formsResult = await getDiscardedForms();
    if (formsResult.success) {
      setDiscardedForms(formsResult.data);
    } else {
      setError('فشل تحميل النماذج المهملة');
    }

    // Fetch statistics
    const statsResult = await getDiscardedFormsStats();
    if (statsResult.success) {
      setStats(statsResult.stats);
    }

    setLoading(false);
  };

  const handleDelete = async (formId) => {
    setDeleting(true);
    const result = await deleteDiscardedFormById(formId);
    
    if (result.success) {
      // Remove from local state
      setDiscardedForms(discardedForms.filter(form => form.id !== formId));
      // Close modals
      setDeleteConfirm(null);
      setSelectedForm(null);
      // Refresh stats
      const statsResult = await getDiscardedFormsStats();
      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    } else {
      setError('فشل حذف النموذج');
    }
    
    setDeleting(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCompletionPercentage = (form) => {
    const total = form.filled_fields.length + form.missing_fields.length;
    if (total === 0) return 0;
    return Math.round((form.filled_fields.length / total) * 100);
  };

  const exportToCSV = () => {
    if (discardedForms.length === 0) {
      alert('لا توجد بيانات للتصدير');
      return;
    }

    // Define CSV headers
    const headers = [
      'الاسم الثنائي',
      'البريد الإلكتروني',
      'ايدي nsave',
      'العمر',
      'المجال',
      'الجنس',
      'المهارات',
      'كود الإحالة',
      'موافقة شروط الإحالة',
      'الحقول المملوءة',
      'الحقول المفقودة',
      'نسبة الإكمال',
      'Session ID',
      'تاريخ الإنشاء',
      'آخر تحديث'
    ];

    // Convert forms to CSV rows
    const rows = discardedForms.map(form => {
      const completionPercentage = getCompletionPercentage(form);
      return [
        form.full_name || '',
        form.email || '',
        form.nsave_id || '',
        form.age || '',
        form.field || '',
        form.gender || '',
        form.skills || '',
        form.referral || '',
        form.accepts_referral_terms ? 'نعم' : 'لا',
        form.filled_fields?.join(', ') || '',
        form.missing_fields?.join(', ') || '',
        `${completionPercentage}%`,
        form.session_id || '',
        form.created_at ? new Date(form.created_at).toLocaleString('ar-EG') : '',
        form.last_updated ? new Date(form.last_updated).toLocaleString('ar-EG') : ''
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape cells that contain commas, quotes, or newlines
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `discarded_forms_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue border-2 border-yellow p-8 rounded-2xl max-w-md">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle className="text-yellow" size={32} />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-yellow">خطأ</h2>
            <p className="text-white/80 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="px-6 py-2 bg-yellow text-[#05339c] rounded-lg hover:bg-yellow/80 transition-all duration-300 font-semibold flex items-center gap-2 mx-auto"
            >
              <FiRefreshCw size={16} />
              إعادة المحاولة
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          النماذج <span className="text-white">المهملة</span>
        </h1>
        <p className="text-white/70">
          النماذج التي بدأ المستخدمون في ملئها لكن لم يتم إرسالها
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiFileText size={28} className="text-[#05339c]" />
              </div>
              <FiTrendingDown className="text-yellow" size={20} />
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">{stats.total}</p>
            <p className="text-white/70">نماذج مهملة</p>
          </div>
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiPieChart size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-green-400">{stats.averageFilled}</p>
            <p className="text-white/70">متوسط حقول مملوءة</p>
          </div>
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiAlertCircle size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-red-400">{stats.averageMissing}</p>
            <p className="text-white/70">متوسط حقول مفقودة</p>
          </div>
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiPieChart size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">
              {stats.total > 0 
                ? Math.round((stats.averageFilled / (parseFloat(stats.averageFilled) + parseFloat(stats.averageMissing))) * 100)
                : 0}%
            </p>
            <p className="text-white/70">معدل الإكمال</p>
          </div>
        </div>
      )}

      {/* Most Commonly Missed Fields */}
      {stats && stats.missedFieldsCount && Object.keys(stats.missedFieldsCount).length > 0 && (
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-bold text-yellow mb-6">الحقول الأكثر تجاهلاً</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(stats.missedFieldsCount)
              .sort((a, b) => b[1] - a[1])
              .map(([field, count]) => (
                <div key={field} className="bg-red-500/20 rounded-xl p-4 border-2 border-red-500">
                  <div className="text-sm font-medium text-white mb-1">{field}</div>
                  <div className="text-3xl font-bold text-red-400">{count}</div>
                  <div className="text-xs text-white/60 mt-1">
                    {stats.total > 0 ? Math.round((count / stats.total) * 100) : 0}% من النماذج
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Discarded Forms Table */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden">
        <div className="px-6 py-4 bg-yellow flex justify-between items-center">
          <h2 className="text-xl font-bold text-[#05339c]">جميع النماذج المهملة ({discardedForms.length})</h2>
          {discardedForms.length > 0 && (
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-[#05339c] text-yellow rounded-lg hover:bg-blue transition-all duration-300 font-semibold flex items-center gap-2"
            >
              <FiDownload size={18} />
              تصدير إلى CSV
            </button>
          )}
        </div>
        
        {discardedForms.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiFileText className="text-white/50" size={32} />
            </div>
            <p className="text-white mb-2">لا توجد نماذج مهملة</p>
            <p className="text-white/60 text-sm">جميع المستخدمين أكملوا النماذج</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-blue">
                <tr>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    المعلومات الأساسية
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    نسبة الإكمال
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    الحقول المملوءة
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    الحقول المفقودة
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    آخر تحديث
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    تفاصيل
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    حذف
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {discardedForms.map((form) => {
                  const completionPercentage = getCompletionPercentage(form);
                  return (
                    <tr key={form.id} className="hover:bg-blue/20 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-semibold text-white">{form.full_name || 'غير مذكور'}</div>
                          <div className="text-white/60">{form.email || 'غير مذكور'}</div>
                          {form.nsave_id && (
                            <div className="text-white/50 text-xs">ID: {form.nsave_id}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-full bg-white/10 rounded-full h-2.5" style={{ width: '100px' }}>
                            <div 
                              className={`h-2.5 rounded-full transition-all ${
                                completionPercentage >= 75 ? 'bg-green-500' :
                                completionPercentage >= 50 ? 'bg-yellow' :
                                completionPercentage >= 25 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${completionPercentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-white">{completionPercentage}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {form.filled_fields?.length > 0 ? (
                            form.filled_fields.slice(0, 3).map((field, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500">
                                ✓ {field}
                              </span>
                            ))
                          ) : (
                            <span className="text-white/40 text-xs">لا يوجد</span>
                          )}
                          {form.filled_fields?.length > 3 && (
                            <span className="text-white/60 text-xs">+{form.filled_fields.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {form.missing_fields?.length > 0 ? (
                            form.missing_fields.slice(0, 3).map((field, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500">
                                ✗ {field}
                              </span>
                            ))
                          ) : (
                            <span className="text-white/40 text-xs">لا يوجد</span>
                          )}
                          {form.missing_fields?.length > 3 && (
                            <span className="text-white/60 text-xs">+{form.missing_fields.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/60">
                        {formatDate(form.last_updated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setSelectedForm(selectedForm?.id === form.id ? null : form)}
                          className="px-3 py-2 bg-blue text-yellow border-2 border-yellow rounded-lg hover:bg-blue/80 transition-colors font-medium"
                        >
                          {selectedForm?.id === form.id ? 'إخفاء' : 'عرض'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setDeleteConfirm(form)}
                          className="px-3 py-2 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-lg hover:bg-red-500/30 transition-colors font-medium flex items-center gap-2"
                        >
                          <FiTrash2 size={16} />
                          حذف
                        </button>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Selected Form Details Modal */}
      {selectedForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#05339c]">تفاصيل النموذج المهمل</h3>
              <button
                onClick={() => setSelectedForm(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-blue rounded-xl p-4 border-2 border-white/10 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">الاسم الثنائي</label>
                    <div className="text-base font-medium text-white">{selectedForm.full_name || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">البريد الإلكتروني</label>
                    <div className="text-base font-medium text-white">{selectedForm.email || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">ايدي nsave</label>
                    <div className="text-base font-medium text-white">{selectedForm.nsave_id || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">العمر</label>
                    <div className="text-base font-medium text-white">{selectedForm.age || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">المجال</label>
                    <div className="text-base font-medium text-white">{selectedForm.field || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">الجنس</label>
                    <div className="text-base font-medium text-white">{selectedForm.gender || '-'}</div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-white/50 mb-1">المهارات</label>
                    <div className="text-base font-medium text-white">{selectedForm.skills || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">كود الإحالة</label>
                    <div className="text-base font-medium text-yellow">{selectedForm.referral || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">موافقة شروط الإحالة</label>
                    <div className="text-base font-medium text-white">
                      {selectedForm.accepts_referral_terms ? (
                        <span className="text-green-400">✓ نعم</span>
                      ) : (
                        <span className="text-red-400">✗ لا</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                <h4 className="text-sm font-medium text-yellow mb-3">معلومات إضافية</h4>
                <div className="text-xs text-white/60 space-y-2">
                  <div>Session ID: {selectedForm.session_id}</div>
                  <div>تم الإنشاء: {formatDate(selectedForm.created_at)}</div>
                  <div>آخر تحديث: {formatDate(selectedForm.last_updated)}</div>
                  {selectedForm.user_agent && (
                    <div className="truncate">User Agent: {selectedForm.user_agent}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-red-500 rounded-2xl max-w-md w-full">
            <div className="bg-red-500/20 px-6 py-4 border-b-2 border-red-500">
              <h3 className="text-xl font-bold text-red-400 flex items-center gap-2">
                <FiAlertCircle size={24} />
                تأكيد الحذف
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-white mb-4">
                هل أنت متأكد من حذف هذا النموذج؟
              </p>
              <div className="bg-blue/30 rounded-lg p-3 mb-6 border-2 border-white/10">
                <div className="text-sm text-white/80">
                  <div className="font-semibold text-yellow mb-1">
                    {deleteConfirm.full_name || 'غير مذكور'}
                  </div>
                  <div className="text-white/60 text-xs">
                    {deleteConfirm.email || 'غير مذكور'}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-blue/20 text-white rounded-lg hover:bg-blue/30 transition-colors font-medium border-2 border-white/20"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={16} />
                      حذف نهائياً
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default DiscardedForms;

